/**
 * Calculate the fitted font size using browser canvas measureText(),
 * replicating the exact same word-wrapping + auto-shrink algorithm
 * used by the backend (image-generator.service.ts).
 *
 * This ensures the preview text layout matches the generated image.
 */
export function calculateFittedFontSize(
  content: string,
  fontSize: number,
  fontFamily: string | undefined,
  displayWidth: number,
  displayHeight: number,
  paddingRatio: number = 0.08,
): number {
  if (!content || !fontSize || !displayWidth || !displayHeight) return fontSize;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return fontSize;

  const font = fontFamily ? `"${fontFamily}", sans-serif` : 'sans-serif';
  const maxTextWidth = displayWidth * (1 - 2 * paddingRatio);
  const maxTextHeight = displayHeight * (1 - 2 * paddingRatio);

  // Word-wrap using measureText — identical to backend wrapText()
  const countLines = (text: string, maxWidth: number, fSize: number): number => {
    ctx.font = `700 ${fSize}px ${font}`;
    const words = text.split(' ');
    let lineCount = 0;
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        lineCount++;
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lineCount++;
    return lineCount;
  };

  let fitFontSize = fontSize;
  let lineCount = countLines(content, maxTextWidth, fitFontSize);
  let totalH = lineCount * fitFontSize * 1.5; // lineHeight = 1.5

  console.log('📏 Auto-fit START:', {
    fontSize,
    fontFamily,
    displayWidth,
    displayHeight,
    maxTextWidth,
    maxTextHeight,
    initialLineCount: lineCount,
    initialTotalH: totalH,
    needsShrink: totalH > maxTextHeight
  });

  // Shrink by 10% each iteration until text fits (same as backend)
  let iterations = 0;
  while (totalH > maxTextHeight && fitFontSize > 12) {
    fitFontSize = Math.round(fitFontSize * 0.9);
    lineCount = countLines(content, maxTextWidth, fitFontSize);
    totalH = lineCount * fitFontSize * 1.5;
    iterations++;
    if (iterations <= 3) {
      console.log(`  Iteration ${iterations}: fontSize=${fitFontSize}px, lines=${lineCount}, totalH=${totalH}px`);
    }
  }

  console.log('📏 Auto-fit END:', {
    finalFontSize: fitFontSize,
    finalLineCount: lineCount,
    finalTotalH: totalH,
    iterations
  });

  return fitFontSize;
}
